import { action, computed, makeObservable, observable, runInAction } from 'mobx';

import { inject, injectable, Store } from '@servicetitan/react-ioc';

import {
    FileUploadResultVm,
    IMobileMaintenanceServiceEntryDetailsVm,
    ServiceEntryCategory,
} from '@fleet-pro/mobile-webview-api';

import { SingleSelectOption } from '../../common/components/single-select';
import { RequestState } from '../../common/utils/request-states';
import { getCategoryText } from '../utils/get-category-text';
import type { ServiceEntryFormType } from '../utils/service-entry-form-state';
import {
    apiDataToServiceEntryFormState,
    formStateToCreateServiceEntryApiData,
    formStateToUpdateServiceEntryApiData,
    ServiceEntryFileDescriptor,
    serviceEntryForm,
    serviceRecordFormState,
    UploadFileDescriptor,
} from '../utils/service-entry-form-state';
import { validateFiles } from '../utils/validate-files';
import { ServiceEntryDataStore } from './service-entry-data.store';

@injectable()
export class ServiceEntryViewStore extends Store {
    @observable serviceEntryForm: ServiceEntryFormType = serviceEntryForm();
    @observable currentServiceEntryId: number | undefined;
    @observable hasFileValidationError = false;
    @observable selectedVehicleName: string | undefined;

    constructor(
        @inject(ServiceEntryDataStore)
        private readonly serviceEntryDataStore: ServiceEntryDataStore
    ) {
        super();
        makeObservable(this);
    }

    // ========== Computed Properties ==========

    @computed get isEditMode() {
        return this.currentServiceEntryId !== undefined;
    }

    @computed get selectedVehicleDisplayName() {
        return this.selectedVehicleName ?? 'N/A';
    }

    @computed get totalSumServiceRecordsCost() {
        return this.serviceEntryForm.$.serviceRecords.$.reduce((sum, serviceRecord) => {
            return sum + (serviceRecord.$.cost.value ?? 0);
        }, 0);
    }

    @computed get isSubmitting() {
        return (
            this.serviceEntryDataStore.createServiceEntryRequestState === RequestState.Loading ||
            this.serviceEntryDataStore.updateServiceEntryRequestState === RequestState.Loading ||
            this.serviceEntryDataStore.uploadFilesRequestState === RequestState.Loading
        );
    }

    @computed get isServiceEntryCreated() {
        return this.serviceEntryDataStore.createServiceEntryRequestState === RequestState.Success;
    }

    @computed get isServiceEntryUpdated() {
        return this.serviceEntryDataStore.updateServiceEntryRequestState === RequestState.Success;
    }

    /**
     * For preview page - formatted service entry data
     */
    @computed get serviceEntry() {
        return {
            vehicleName: this.serviceEntryDataStore.serviceEntry?.vehicleName ?? '—',
            vin: this.serviceEntryDataStore.serviceEntry?.vin ?? '—',
            category: getCategoryText(
                this.serviceEntryDataStore.serviceEntry?.category ??
                    ServiceEntryCategory.Unscheduled
            ),
            serviceDate:
                this.serviceEntryDataStore.serviceEntry?.serviceDate?.toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric',
                }) ?? '',
            odometer: Math.round(
                this.serviceEntryDataStore.serviceEntry?.odometerInMiles ?? 0
            ).toFixed(0),
            engineHours: Math.round(
                this.serviceEntryDataStore.serviceEntry?.engineRunTimeInHours ?? 0
            ).toFixed(0),
            serviceRecords: this.serviceEntryDataStore.serviceEntry?.serviceRecords?.map(
                serviceRecord => ({
                    serviceType: serviceRecord.serviceType,
                    cost: serviceRecord.cost,
                })
            ),
            serviceCenter: this.serviceEntryDataStore.serviceEntry?.serviceCenter ?? '—',
            invoiceNumber: this.serviceEntryDataStore.serviceEntry?.invoiceNumber ?? '—',
            notes: this.serviceEntryDataStore.serviceEntry?.notes ?? '—',
            fileAttachments: this.serviceEntryDataStore.serviceEntry?.fileAttachments,
            totalCost: this.serviceEntryDataStore.serviceEntry?.totalCost ?? 0,
        };
    }

    @computed get serviceTypeOptions(): SingleSelectOption[] {
        return (
            this.serviceEntryDataStore.serviceTypes?.map(serviceType => ({
                id: serviceType.id.toString(),
                label: serviceType.name,
            })) ?? []
        );
    }

    @computed get vinOptions(): SingleSelectOption[] {
        return (
            this.serviceEntryDataStore.vehicles?.map(vehicle => ({
                id: vehicle.vehicleName ?? '',
                label: vehicle.vin,
            })) ?? []
        );
    }

    get categoryOptions(): SingleSelectOption[] {
        return [
            {
                id: ServiceEntryCategory.Unscheduled,
                label: getCategoryText(ServiceEntryCategory.Unscheduled),
            },
            {
                id: ServiceEntryCategory.Scheduled,
                label: getCategoryText(ServiceEntryCategory.Scheduled),
            },
            {
                id: ServiceEntryCategory.Repairs,
                label: getCategoryText(ServiceEntryCategory.Repairs),
            },
        ];
    }

    isStepValid = (step: string) => {
        if (!this.serviceEntryForm?.$) {
            return false;
        }
        
        const form = this.serviceEntryForm.$;

        switch (step) {
            case 'service-details':
                if (!form.vin || !form.category || !form.serviceDate) {
                    return false;
                }

                return !!(
                    form.vin.value &&
                    form.category.value &&
                    form.serviceDate.value
                );

            case 'services':
                if (!form.serviceRecords || !form.serviceRecords.$) {
                    return false;
                }

                const serviceRecords = form.serviceRecords.$;
                if (!Array.isArray(serviceRecords) || serviceRecords.length === 0) {
                    return false;
                }

                return serviceRecords.every(record => {
                    try {
                        if (!record || !record.$ || !record.$.serviceType) {
                            return false;
                        }
                        return !!record.$.serviceType.value;
                    } catch {
                        return false;
                    }
                });

            case 'additional-details':
                return true;
            default:
                return false;
        }
    }

    // ========== Actions ==========

    @action resetForm = () => {
        this.serviceEntryForm = serviceEntryForm();
        this.currentServiceEntryId = undefined;
        this.hasFileValidationError = false;
        this.selectedVehicleName = undefined;
    };

    @action initEditMode = (serviceEntryData: IMobileMaintenanceServiceEntryDetailsVm) => {
        this.currentServiceEntryId = serviceEntryData.id;
        this.serviceEntryForm = apiDataToServiceEntryFormState(serviceEntryData);
        this.selectedVehicleName = serviceEntryData.vehicleName;
    };

    @action addServiceRecord = () => {
        this.serviceEntryForm.$.serviceRecords.$.push(serviceRecordFormState());
    };

    @action removeServiceRecord = (index: number) => {
        if (this.serviceEntryForm.$.serviceRecords.$.length > 1) {
            this.serviceEntryForm.$.serviceRecords.$.splice(index, 1);
        }
    };

    @action handleVinChange = (item: SingleSelectOption | null) => {
        this.selectedVehicleName = item?.id.toString();
        this.serviceEntryForm.$.vin.onChange(item?.label ?? undefined);
        this.serviceEntryDataStore.clearVehicles();
    };

    @action handleFileChange = (newFiles: FileList | File[]) => {
        const filesArray = Array.isArray(newFiles)
            ? newFiles
            : Array.from(newFiles);

        const files: ServiceEntryFileDescriptor[] = [
            ...this.serviceEntryForm.$.fileAttachments.value,
            ...filesArray.map<UploadFileDescriptor>(file => ({
                file,
                uploadId: '',
            })),
        ];

        runInAction(() => {
            this.hasFileValidationError = validateFiles(files);
        });

        if (!this.hasFileValidationError) {
            this.serviceEntryForm.$.fileAttachments.onChange(files);
        }
    };

    @action handleFileDelete = (file: ServiceEntryFileDescriptor) => {
        this.serviceEntryForm.$.fileAttachments.onChange(
            this.serviceEntryForm.$.fileAttachments.value.filter(value => value !== file)
        );
        this.hasFileValidationError = false;
    };

    @action attachUploadedFiles = (files: FileUploadResultVm[]) => {
        const existingAttachedFiles = this.serviceEntryForm.$.fileAttachments.value.filter(
            (item): item is ServiceEntryFileDescriptor => typeof item.file === 'string'
        );

        const newUploadedFiles: UploadFileDescriptor[] = files.map(item => ({
            file: new File([], item.name),
            displayName: item.name,
            uploadId: item.uploadId,
        }));

        this.serviceEntryForm.$.fileAttachments.onChange([
            ...existingAttachedFiles,
            ...newUploadedFiles,
        ]);
    };

    // ========== Submit Handlers ==========

    handleFileUpload = async (): Promise<void> => {
        const newFiles = this.serviceEntryForm.$.fileAttachments.value.filter(
            item => item.file instanceof File
        );

        if (!newFiles.length) {
            return;
        }

        const files = await this.serviceEntryDataStore.uploadFiles(newFiles);
        this.attachUploadedFiles(files);
    };

    handleSubmit = async (): Promise<boolean> => {
        const { hasError } = await this.serviceEntryForm.enableAutoValidationAndValidate();

        if (hasError) {
            return false;
        }

        await this.handleFileUpload();

        if (this.isEditMode && this.currentServiceEntryId) {
            const serviceEntry = formStateToUpdateServiceEntryApiData(
                this.serviceEntryForm,
                this.currentServiceEntryId
            );
            await this.serviceEntryDataStore.updateServiceEntry(serviceEntry);
        } else {
            const serviceEntry = formStateToCreateServiceEntryApiData(this.serviceEntryForm);
            await this.serviceEntryDataStore.createServiceEntry(serviceEntry);
        }

        return true;
    };
}
